import React, { useEffect, useReducer, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Modal from 'react-modal';
import classNames from 'classnames';
import device from 'common/support/device';

import { FocusGroup, FocusTrap } from 'components/focus';
import Button from 'components/common/Button';
import LogoPage from 'components/pages/LogoPage';
import DividedContent from 'components/common/DividedContent';

import focusManager from 'support/focus/FocusManager';
import textToSpeechManager from 'support/tts/TextToSpeechManager';
import {
  setDialogOpen,
  setDialogClosed,
  setRequestedFocusId,
} from 'actions/dialog';

import type { VideoCtvRedux } from 'store-types';

import styles from './Dialog.module.scss';

interface DialogProps {
  isOpen: boolean;
  title?: string;
  message?: string;
  backgroundUrl?: string | null;
  positiveCTA?: string;
  onPositive?: (() => void) | null;
  negativeCTA?: string;
  onNegative?: (() => void) | null;
  horizontal?: boolean;
  injectHtml?: boolean;
  onActionOverride?: (action: string) => boolean;
  initialFocus?: 'positive' | 'negative';
}

interface LocalState {
  isOpen: boolean;
}

type LocalAction = { type: 'IS_OPEN_CHANGED' };

const Dialog = ({
  title = '',
  message = '',
  isOpen,
  backgroundUrl = null,
  positiveCTA = 'Okay',
  onPositive = null,
  negativeCTA = 'Back',
  onNegative = null,
  horizontal = false,
  injectHtml = false,
  onActionOverride = () => false,
  initialFocus = 'positive',
}: DialogProps) => {
  const dispatch = useDispatch();

  const isAnotherDialogOpen = useSelector((state: VideoCtvRedux) => state.dialog.isOpen);
  const [state, localDispatch] = useReducer(
    (prevState: LocalState, action: LocalAction): LocalState => {
      switch (action.type) {
        case 'IS_OPEN_CHANGED':
          return {
            isOpen: isOpen && !isAnotherDialogOpen,
          };
        default:
          return prevState;
      }
    },
    {
      isOpen: isOpen && !isAnotherDialogOpen,
    },
  );

  useEffect(() => {
    localDispatch({ type: 'IS_OPEN_CHANGED' });
  }, [isOpen, localDispatch]);

  const latestFocusId = useRef<string | null>(null);

  const requestedFocusId = useSelector(
    (reduxState: VideoCtvRedux) => reduxState.dialog.requestedFocusId,
  );
  useEffect(() => {
    latestFocusId.current = requestedFocusId;
  });

  useEffect(() => {
    if (state.isOpen) {
      dispatch(setRequestedFocusId(focusManager.currentFocusId as string));
    }
  }, [state.isOpen, dispatch]);

  useEffect(() => {
    if (!state.isOpen && latestFocusId.current) {
      focusManager.setCurrentFocus(latestFocusId.current);
    }
  }, [state.isOpen]);

  useEffect(() => {
    if (state.isOpen) {
      dispatch(setDialogOpen());
      textToSpeechManager.clear();
      textToSpeechManager.add(title);
      textToSpeechManager.add(message);

      if (initialFocus === 'negative' && onNegative) {
        focusManager.setCurrentFocus('dialog-negative-button');
      } else if (onPositive) {
        focusManager.setCurrentFocus('dialog-positive-button');
      } else if (onNegative) {
        focusManager.setCurrentFocus('dialog-negative-button');
      }
    }
  });

  useEffect(() => {
    return () => {
      if (state.isOpen) {
        dispatch(setDialogClosed());
      }
    };
  }, [dispatch, state.isOpen]);

  useEffect(() => {
    return () => {
      textToSpeechManager.stop();
    };
  }, []);

  return (
    <Modal
      isOpen={state.isOpen}
      aria={
        {
          hidden: !device.isSamsungTizen,
        } as React.ComponentProps<typeof Modal>['aria']
      }
      overlayClassName={classNames(
        styles.overlay,
        'app-scalable',
        'fixed-top fixed-bottom',
        'd-flex justify-content-center align-items-center',
      )}
      className={classNames(
        'text-white',
        'w-100 h-100',
        'd-flex justify-content-center align-items-center',
      )}
      {...(backgroundUrl
        ? { style: { content: { backgroundImage: `url(${backgroundUrl})` } } }
        : {})}
    >
      <FocusTrap
        className={classNames(
          'w-100 h-100',
          'd-flex flex-column align-items-center justify-content-center',
        )}
      >
        <LogoPage className="position-relative">
          <DividedContent title={title}>
            <div
              className={styles.message}
              data-testid="dialog-message"
              // We allow for setting inner HTML in certain cases
              // since there are requirements around allowing dynamic
              // formatting for our dialogues.
              // eslint-disable-next-line react/no-danger
              {...(injectHtml
                ? { dangerouslySetInnerHTML: { __html: message } }
                : {})}
            >
              {!injectHtml ? message : null}
            </div>
            <FocusGroup
              horizontal={horizontal}
              onActionOverride={onActionOverride}
            >
              {onPositive && (
                <Button
                  className={classNames(
                    'dialog-positiveButton',
                    styles.dialogButton,
                  )}
                  onClick={onPositive}
                  size="md"
                  ttsText={positiveCTA}
                  focusId="dialog-positive-button"
                >
                  {positiveCTA}
                </Button>
              )}
              {onNegative && (
                <Button
                  className={classNames(
                    'dialog-negativeButton',
                    styles.dialogButton,
                  )}
                  onClick={onNegative}
                  size="md"
                  ttsText={negativeCTA}
                  focusId="dialog-negative-button"
                >
                  {negativeCTA}
                </Button>
              )}
            </FocusGroup>
          </DividedContent>
        </LogoPage>
      </FocusTrap>
    </Modal>
  );
};

export default Dialog;
